import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Field, Form as FinalForm } from 'react-final-form';
import validator from 'validator';
import {
  Button,
  Dropdown,
  Form,
  FormField,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from 'semantic-ui-react';
import ValidatedFormInput from 'components/ValidatedFormInput';
import { createDropdownItemByValueAndText } from 'utils/reactSemanticUtils';
import { composeValidators } from 'utils/formUtils';

const requiredStringValidation = (value = '') =>
  validator.isEmpty(value.trim()) ? 'This field is required' : undefined;

const requiredIntegerValidation = value =>
  !value ? 'This field is required' : undefined;

const accountHolderNameLettersOnly = (value = '') =>
  /^[a-zA-Z\s]+$/.test(value) ? undefined : 'Must be letters and spaces only';

const numericOnlyNormalization = value => {
  if (!value) {
    return '';
  }
  return value.replace(/[^\d]/g, '');
};

const formMutatorObject = {
  setValue: ([field, value], state, { changeValue }) =>
    changeValue(state, field, () => value),
};

function EditBankAccountModal({ shown, onSubmit, onClose, isLoading }) {
  let submit;
  // let formMutators;
  let formInitialize;

  const banks = useSelector(state => state.global.banks);
  const initialValues = useSelector(
    state => state.kyb.businessDetail.bankAccount
  );

  useEffect(() => {
    if (shown) {
      formInitialize(initialValues || {});
    }
  }, [formInitialize, initialValues, shown]);

  const banksAsDropdownItems = useMemo(
    () => banks.map(b => createDropdownItemByValueAndText(b.id, b.name)),
    [banks]
  );

  const renderForm = ({ handleSubmit, form: { initialize } }) => {
    submit = handleSubmit;
    formInitialize = initialize;
    return (
      <Form>
        <FormField>
          <label htmlFor="account-holder-name">Bank Account Holder Name</label>
          <Field
            id="account-holder-name"
            name="account_holder_name"
            component={ValidatedFormInput}
            validate={composeValidators(
              requiredStringValidation,
              accountHolderNameLettersOnly
            )}
          />
        </FormField>
        <FormField>
          <label htmlFor="bank-id">Bank Name</label>
          <Field
            id="bank-id"
            name="bank_id"
            validate={requiredIntegerValidation}
          >
            {({ input, meta, ...rest }) => {
              const errorContent = meta.touched && !!meta.error && meta.error;
              return (
                <FormField error={errorContent}>
                  <Dropdown
                    {...input}
                    onChange={(e, { value }) => input.onChange(value)}
                    {...rest}
                    placeholder="Select Bank"
                    fluid
                    selection
                    options={banksAsDropdownItems}
                  />
                  {errorContent && (
                    <Label prompt content={errorContent} pointing="above" />
                  )}
                </FormField>
              );
            }}
          </Field>
        </FormField>
        <FormField>
          <label htmlFor="account-number">Bank Account Number</label>
          <Field
            id="account-number"
            name="account_holder_number"
            component={ValidatedFormInput}
            validate={requiredStringValidation}
            parse={numericOnlyNormalization}
          />
        </FormField>
      </Form>
    );
  };

  renderForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
  };

  return (
    <Modal open={shown}>
      <ModalHeader>Edit Bank Account</ModalHeader>
      <ModalContent>
        <FinalForm
          onSubmit={onSubmit}
          render={renderForm}
          mutators={formMutatorObject}
        />
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          positive
          onClick={event => submit(event)}
          disabled={isLoading}
          loading={isLoading}
        >
          Confirm
        </Button>
      </ModalActions>
    </Modal>
  );
}

EditBankAccountModal.propTypes = {
  shown: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default EditBankAccountModal;
